import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Badge,
  Card,
  Row,
  Col,
  Form,
  InputGroup,
  Alert,
} from "react-bootstrap";
import {
  Search,
  Edit,
  Eye,
  RefreshCw,
  Package,
  Calendar,
  Download,
} from "lucide-react";
import { getAllOrders } from "../../api/orders";
import useAuth from "../../hooks/useAuth";
// Importation des composants react-pdf
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  pdf,
} from "@react-pdf/renderer";
import { formatCurrency, formatDate } from "../../utils/formatters";

// Configuration - URL de base pour l'API
const API_BASE_URL = process.env.REACT_APP_API_URL || "https://apps.prod.atlanticds-sn.com/senoscar";

// Styles pour le PDF (même style que dans GenererPdfFacture)
const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    padding: 40,
    fontSize: 12,
  },
  header: {
    flexDirection: "row",
    marginBottom: 40,
  },
  logo: {
    width: 150,
    height: "auto",
    marginRight: 20,
  },
  companyInfo: {
    flex: 1,
    textAlign: "right",
    fontSize: 10,
    color: "#333",
  },
  companyName: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 5,
  },
  companyAddress: {
    marginBottom: 2,
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 5,
  },
  dateContainer: {
    alignItems: "flex-end",
    marginBottom: 20,
  },
  clientSection: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 5,
    backgroundColor: "#e0e0e0",
    padding: 4,
  },
  clientDetails: {
    fontSize: 10,
    marginBottom: 2,
  },
  table: {
    marginBottom: 30,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#b5835a", // Couleur bronze/dorée pour l'en-tête
    color: "white",
    padding: 5,
    fontSize: 10,
    fontWeight: "bold",
    borderBottomWidth: 1,
    borderBottomColor: "#dddddd",
    textAlign: "center",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#dddddd",
    padding: 5,
    fontSize: 10,
  },
  tableCell: {
    padding: 3,
  },
  numCell: {
    width: "5%",
    textAlign: "center",
  },
  productCell: {
    width: "35%",
  },
  imageCell: {
    width: "15%",
    alignItems: "center",
  },
  quantityCell: {
    width: "15%",
    textAlign: "center",
  },
  priceCell: {
    width: "15%",
    textAlign: "right",
  },
  totalCell: {
    width: "15%",
    textAlign: "right",
  },
  productImage: {
    width: 40,
    height: 40,
    objectFit: "contain",
  },
  totalRow: {
    flexDirection: "row",
    marginTop: 10,
    borderTopWidth: 1,
    borderTopColor: "#dddddd",
    padding: 5,
  },
  netPayableRow: {
    flexDirection: "row",
    marginTop: 5,
    padding: 5,
    fontWeight: "bold",
  },
  totalLabel: {
    textAlign: "left",
    fontWeight: "bold",
    width: "55%", // Somme des largeurs des 3 premières colonnes (5% + 35% + 15%)
  },
  totalValue: {
    width: "15%",
    textAlign: "right",
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 40,
    right: 40,
    textAlign: "center",
    fontSize: 9,
    color: "#666",
    borderTopWidth: 1,
    borderTopColor: "#dddddd",
    paddingTop: 10,
  },
});

// Fonction identique à celle de GenererPdfFacture pour obtenir l'URL de l'image principale
const getMainImageUrl = (product) => {
  if (!product.images || product.images.length === 0) {
    return null;
  }

  const mainImage = product.images.find((img) => img.mainImage);
  const path = mainImage ? mainImage.path : product.images[0].path;

  // Traiter l'URL correctement selon le format stocké en base
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }

  return `${API_BASE_URL}/uploads/${path}`;
};

// Composant PDF pour les commandes/devis
const OrderPDF = ({ order, isInvoice }) => {
  // Calcul des totaux
  const totalQuantity = order.items.reduce(
    (sum, item) => sum + item.quantity,
    0
  );
  const totalHT = order.totalAmount;

  // Vérifier si la TVA est applicable pour ce client
  const isTVAApplicable = order.partner?.vatApplicable === true;
  const tvaRate = isTVAApplicable ? 0.18 : 0;
  const tva = totalHT * tvaRate;
  const totalTTC = totalHT + tva;

  // Obtenir la date formatée
  const formattedDate = formatDate(order.orderDate);

  // Déterminer le type de document
  const documentType = isInvoice ? "FACTURE" : "DEVIS";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* En-tête avec logo et informations de l'entreprise */}
        <View style={styles.header}>
          <Image src="/senoscar/images/logoSO.png" style={styles.logo} />
          <View style={styles.companyInfo}>
            <Text style={styles.companyName}>
              Zone Industrielle, Diamniadio,
            </Text>
            <Text style={styles.companyAddress}>
              En Face Du Marché D'intérêt National,
            </Text>
            <Text style={styles.companyAddress}>
              Dakar, Sénégal, Tel: +221 78 655 88 88
            </Text>
          </View>
        </View>

        {/* Titre du document et date */}
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            marginBottom: 20,
          }}
        >
          <Text style={styles.title}>
            {documentType} N° {order.orderNumber}
          </Text>
          <Text>{formattedDate}</Text>
        </View>

        {/* Section client */}
        <View style={styles.clientSection}>
          <Text style={styles.sectionTitle}>CLIENT</Text>
          <Text style={styles.clientDetails}>{order.partner.name}</Text>
          <Text style={styles.clientDetails}>
            {order.partner.address || "Adresse non spécifiée"}
          </Text>
          <Text style={styles.clientDetails}>{order.partner.email || ""}</Text>
        </View>

        {/* Tableau des produits */}
        <View style={styles.table}>
          {/* En-tête du tableau */}
          <View style={styles.tableHeader}>
            <Text style={[styles.tableCell, styles.numCell]}>N°</Text>
            <Text style={[styles.tableCell, styles.productCell]}>PRODUIT</Text>
            <Text style={[styles.tableCell, styles.imageCell]}>IMAGE</Text>
            <Text style={[styles.tableCell, styles.quantityCell]}>
              QUANTITE
            </Text>
            <Text style={[styles.tableCell, styles.priceCell]}>PU</Text>
            <Text style={[styles.tableCell, styles.totalCell]}>TOTAL</Text>
          </View>

          {/* Corps du tableau */}
          {order.items.map((item, index) => (
            <View key={index} style={styles.tableRow}>
              <Text style={[styles.tableCell, styles.numCell]}>
                {index + 1}
              </Text>
              <Text style={[styles.tableCell, styles.productCell]}>
                {item.product.sku} / {item.product.name}
              </Text>
              <View style={[styles.tableCell, styles.imageCell]}>
                {getMainImageUrl(item.product) && (
                  <Image
                    src={getMainImageUrl(item.product)}
                    style={styles.productImage}
                  />
                )}
              </View>
              <Text style={[styles.tableCell, styles.quantityCell]}>
                {item.quantity}
              </Text>
              <Text style={[styles.tableCell, styles.priceCell]}>
                {formatCurrency(item.unitPrice, false)}
              </Text>
              <Text style={[styles.tableCell, styles.totalCell]}>
                {formatCurrency(item.quantity * item.unitPrice, false)}
              </Text>
            </View>
          ))}

          {/* Ligne des totaux */}
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>TOTAL</Text>
            <Text style={[styles.tableCell, styles.quantityCell]}>
              {totalQuantity}
            </Text>
            <Text style={[styles.tableCell, styles.priceCell]}></Text>
            <Text style={styles.totalCell}>
              {formatCurrency(totalHT, false)}
            </Text>
          </View>

          {/* Ligne TVA si applicable */}
          {isTVAApplicable && (
            <View style={styles.tableRow}>
              <Text style={styles.totalLabel}></Text>
              <Text style={[styles.tableCell, styles.quantityCell]}></Text>
              <Text style={[styles.tableCell, styles.priceCell]}>
                TVA (18%)
              </Text>
              <Text style={styles.totalCell}>{formatCurrency(tva, false)}</Text>
            </View>
          )}

          {/* Ligne du net à payer */}
          <View style={styles.netPayableRow}>
            <Text style={styles.totalLabel}></Text>
            <Text style={[styles.tableCell, styles.quantityCell]}></Text>
            <Text>NET A PAYER</Text>
            <Text style={styles.totalCell}>
              {formatCurrency(isTVAApplicable ? totalTTC : totalHT, true)}
            </Text>
          </View>
        </View>

        {/* Texte supplémentaire pour les devis */}
        {!isInvoice && (
          <Text style={{ fontSize: 10, fontStyle: "italic", marginTop: 20 }}>
            Ce devis est valable 05 jours à compter de sa date d'émission.
          </Text>
        )}

        {/* Pied de page */}
        <View style={styles.footer}>
          <Text>
            Zone Industrielle, Diamniadio, En Face Du Marché D'intérêt National,
            Dakar, Sénégal
          </Text>
          <Text>Tel: +221 78 655 88 88</Text>
        </View>
      </Page>
    </Document>
  );
};

const OrderList = ({ onViewOrder, onCreateOrder }) => {
  const { isAdmin, isManager } = useAuth();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState("ALL");

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        setError("");

        const data = await getAllOrders();
        const sortedData = [...data].sort((a, b) => {
          const isPendingA = a.status === "PENDING" ? 1 : 0;
          const isPendingB = b.status === "PENDING" ? 1 : 0;

          // Priorité au statut "en attente"
          if (isPendingB !== isPendingA) {
            return isPendingB - isPendingA;
          }

          // Si le statut est le même, trier par id décroissant
          const idA = Number(a.id);
          const idB = Number(b.id);
          return idB - idA;
        });
        setOrders(sortedData);
      } catch (err) {
        setError("Échec du chargement des commandes. Veuillez réessayer.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [refreshTrigger]);

  useEffect(() => {
    let filtered = [...orders];

    // Filtre par terme de recherche
    if (searchTerm.trim() !== "") {
      filtered = filtered.filter(
        (order) =>
          order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.partner.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtre par statut
    if (selectedStatus !== "ALL") {
      filtered = filtered.filter((order) => order.status === selectedStatus);
    }

    setFilteredOrders(filtered);
  }, [orders, searchTerm, selectedStatus]);

  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  // Nouvelle fonction de génération de PDF avec react-pdf
  const generateDocumentPDF = async (order) => {
    try {
      console.log("Génération du PDF pour la commande:", order.orderNumber);

      // Déterminer si c'est un devis ou une facture
      const isInvoice = ["DELIVERED"].includes(order.status);
      const documentType = isInvoice ? "Facture" : "Devis";

      // Créer le blob PDF
      const blob = await pdf(
        <OrderPDF order={order} isInvoice={isInvoice} />
      ).toBlob();

      // Créer une URL pour le blob
      const url = URL.createObjectURL(blob);

      // Créer un lien de téléchargement
      const link = document.createElement("a");
      link.href = url;
      link.download = `${documentType}_${order.orderNumber}.pdf`;

      // Ajouter temporairement le lien au document, cliquer dessus, puis le supprimer
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Libérer l'URL
      URL.revokeObjectURL(url);

      console.log("PDF généré avec succès");
    } catch (error) {
      console.error("Erreur lors de la génération du PDF:", error);
      alert("Erreur lors de la génération du PDF. Veuillez réessayer.");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "PENDING":
        return <Badge bg="warning">En attente</Badge>;
      case "CONFIRMED":
        return <Badge bg="primary">Confirmé</Badge>;
      case "PROCESSING":
        return <Badge bg="info">En cours de traitement</Badge>;
      case "SHIPPED":
        return <Badge bg="purple">En cours de livraison</Badge>;
      case "DELIVERED":
        return <Badge bg="success">Livré</Badge>;
      case "CANCELLED":
        return <Badge bg="danger">Annulé</Badge>;
      case "REJECTED":
        return <Badge bg="dark">Rejeté</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const formatCurrency = (amount, withSymbol = true) => {
    if (amount === null || amount === undefined) {
      return withSymbol ? "0 FCFA" : "0";
    }

    const formattedValue = Number(amount)
      .toLocaleString("fr-FR")
      .replace(/\s/g, ".");

    return withSymbol ? `${formattedValue} FCFA` : formattedValue;
  };

  // Fonction pour rendre le tableau des commandes
  const renderOrdersTable = (orders) => {
    return (
      <Table striped hover>
        <thead>
          <tr>
            <th>Commande #</th>
            <th>Client</th>
            <th>Date</th>
            <th>Montant</th>
            <th>Statut</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {orders.length > 0 ? (
            [...orders]
              .sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate))
              .map((order) => (
                <tr key={order.id}>
                  <td>
                    <div className="fw-bold">{order.orderNumber}</div>
                    <small className="text-muted d-flex align-items-center">
                      <Package size={12} className="me-1" />
                      {order.items.length} Produits
                    </small>
                  </td>
                  <td>
                    <div>{order.partner.name}</div>
                    <small className="text-muted">{order.partner.email}</small>
                  </td>
                  <td>
                    <div className="small d-flex align-items-center">
                      <Calendar size={12} className="me-1" />
                      {formatDate(order.orderDate)}
                    </div>
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      <span>{formatCurrency(order.totalAmount)}</span>
                    </div>
                    {order.vatAmount > 0 && (
                      <small className="text-muted">
                        Incl. TVA: {formatCurrency(order.vatAmount)}
                      </small>
                    )}
                  </td>
                  <td>{getStatusBadge(order.status)}</td>
                  <td>
                    <Button
                      variant="outline-info"
                      size="sm"
                      className="me-1"
                      onClick={() => onViewOrder(order.id)}
                    >
                      <Eye size={16} />
                    </Button>
                    {(isAdmin || isManager) && order.status === "PENDING" && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-1"
                        onClick={() => onViewOrder(order.id, true)}
                      >
                        <Edit size={16} />
                      </Button>
                    )}
                    <Button
                      variant="outline-dark"
                      size="sm"
                      onClick={() => generateDocumentPDF(order)}
                    >
                      <Download size={16} />
                    </Button>
                  </td>
                </tr>
              ))
          ) : (
            <tr>
              <td colSpan="6" className="text-center py-3">
                Aucune commande trouvée
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    );
  };

  return (
    <Card className="shadow-sm">
      <Card.Header>
        <Row className="align-items-center">
          <Col>
            <h5 className="mb-0">Gestion des commandes</h5>
          </Col>
          <Col xs="auto">
            <Button
              variant="success"
              size="sm"
              className="me-2"
              onClick={onCreateOrder}
            >
              <Package size={16} className="me-1" />
              Nouvelle commande
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleRefresh}
            >
              <RefreshCw size={16} />
            </Button>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        <Row className="mb-3">
          <Col md={8}>
            <InputGroup>
              <InputGroup.Text>
                <Search size={18} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Rechercher par numéro de commande ou nom du client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </Col>
          <Col md={4}>
            <Form.Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="ALL">Tous les status</option>
              <option value="PENDING">En attente</option>
              <option value="CONFIRMED">Confirmé</option>
              <option value="PROCESSING">En cours de traitement</option>
              <option value="SHIPPED">En cours de livraison</option>
              <option value="DELIVERED">Livré</option>
              <option value="CANCELLED">Annulé</option>
              <option value="REJECTED">Rejeté</option>
            </Form.Select>
          </Col>
        </Row>

        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
            <p className="mt-2">Commandes en chargement...</p>
          </div>
        ) : (
          <div className="table-responsive">
            {renderOrdersTable(filteredOrders)}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default OrderList;
